/**
 * Default Theme Configurations
 *
 * This file defines the built-in Dark and Light theme presets,
 * as well as the template for custom user themes.
 */

/**
 * Default Dark Theme Configuration
 */
export const DEFAULT_DARK_THEME = {
  id: 'dark',
  name: 'Dark Mode',
  type: 'dark',
  editable: false,
  colors: {
    // Background colors
    background: {
      primary: '#0a0a0a',      // Deep black
      secondary: '#1a1a1a',    // Dark gray
      tertiary: '#262626',     // Medium gray
      elevated: '#2a2a2a',     // Slightly lighter for cards/modals
      hover: '#333333',        // Hover state
    },

    // Text colors
    text: {
      primary: '#ffffff',      // Pure white
      secondary: '#d4d4d8',    // Light gray
      muted: '#a1a1aa',        // Medium gray
      disabled: '#71717a',     // Disabled state
      inverse: '#0a0a0a',      // For light backgrounds
      onAccent: '#ffffff',     // Text on accent/fixed colored backgrounds (buttons, etc.)
    },

    // Border colors
    border: {
      default: '#374151',      // Default border
      subtle: '#27272a',       // Subtle borders
      strong: '#52525b',       // Strong emphasis
      accent: '#8b5cf6',       // Accent color borders
    },

    // Accent/Brand colors
    accent: {
      primary: '#8b5cf6',      // Purple
      secondary: '#7c3aed',    // Darker purple
      light: '#a78bfa',        // Light purple
      hover: '#7c3aed',        // Hover state
      disabled: '#6d28d9',     // Disabled state
    },

    // Semantic colors
    success: '#10b981',        // Green
    warning: '#f59e0b',        // Amber/Orange
    error: '#ef4444',          // Red
    info: '#3b82f6',           // Blue

    // Interactive states
    hover: '#333333',
    focus: '#8b5cf6',
    active: '#7c3aed',
    disabled: '#3f3f46',

    // Gradients
    gradient: {
      from: '#111827',         // gray-900
      via: '#000000',          // black
      to: '#111827',           // gray-900
      accent: {
        from: '#8b5cf6',       // purple-600
        to: '#7c3aed',         // purple-700
      }
    },

    // Special purpose
    overlay: 'rgba(0, 0, 0, 0.7)',
    shadow: 'rgba(0, 0, 0, 0.5)',
  }
};

/**
 * Default Light Theme Configuration
 */
export const DEFAULT_LIGHT_THEME = {
  id: 'light',
  name: 'Light Mode',
  type: 'light',
  editable: false,
  colors: {
    // Background colors - muted warm sepia tones
    background: {
      primary: '#e8e3d5',      // Muted warm beige (main background)
      secondary: '#dfd9c8',    // Darker warm tan
      tertiary: '#d4cdb9',     // Medium tan/beige
      elevated: '#f0ebe0',     // Slightly lighter cream for cards/modals
      hover: '#dad4c3',        // Warm hover state
    },

    // Text colors - softer, warmer tones
    text: {
      primary: '#2d2820',      // Warm dark brown instead of black
      secondary: '#5a5347',    // Medium warm brown
      muted: '#78716c',        // Muted warm gray
      disabled: '#a8a29e',     // Lighter warm gray for disabled
      inverse: '#e8e3d5',      // Muted warm beige for dark backgrounds
      onAccent: '#ffffff',     // Text on accent/fixed colored backgrounds (buttons, etc.)
    },

    // Border colors - muted warm tones
    border: {
      default: '#c4bcab',      // Muted warm tan border
      subtle: '#d4cdb9',       // Subtle warm border
      strong: '#9a9283',       // Stronger warm border
      accent: '#d4a5a5',       // Accent color borders (soft rose)
    },

    // Accent/Brand colors - soft pink/rose tones
    accent: {
      primary: '#d4a5a5',      // Soft dusty rose
      secondary: '#c08a8a',    // Darker rose
      light: '#e5c4c4',        // Light pink
      hover: '#c08a8a',        // Hover state
      disabled: '#e8d4d4',     // Disabled state
    },

    // Semantic colors
    success: '#10b981',        // Green
    warning: '#f59e0b',        // Amber/Orange
    error: '#ef4444',          // Red
    info: '#3b82f6',           // Blue

    // Interactive states - warm tones
    hover: '#dad4c3',          // Warm hover
    focus: '#d4a5a5',          // Soft rose focus
    active: '#c08a8a',         // Darker rose active
    disabled: '#d4cdb9',       // Warm disabled

    // Gradients - muted warm tones
    gradient: {
      from: '#dfd9c8',         // Darker warm tan
      via: '#f0ebe0',          // Lighter cream
      to: '#dfd9c8',           // Darker warm tan
      accent: {
        from: '#d4a5a5',       // Soft rose
        to: '#c08a8a',         // Darker rose
      }
    },

    // Special purpose - softer shadows
    overlay: 'rgba(45, 40, 32, 0.3)',   // Warm dark overlay
    shadow: 'rgba(45, 40, 32, 0.08)',   // Softer warm shadow
  }
};

/**
 * Empty Custom Theme Template
 * Used as starting point for user-created custom themes
 */
export const EMPTY_CUSTOM_THEME = {
  id: 'custom',
  name: 'My Custom Theme',
  type: 'custom',
  editable: true,
  colors: {
    ...DEFAULT_DARK_THEME.colors // Start from dark as base
  }
};

/**
 * Available theme types
 */
export const THEME_TYPES = {
  DARK: 'dark',
  LIGHT: 'light',
  CUSTOM: 'custom'
};

/**
 * Common color presets for color picker
 */
export const COMMON_COLORS = [
  '#000000', '#ffffff', '#ef4444', '#f59e0b', '#10b981',
  '#3b82f6', '#8b5cf6', '#ec4899', '#64748b', '#78716c'
];

/**
 * Get default theme by type
 */
export function getDefaultTheme(type) {
  switch(type) {
    case THEME_TYPES.LIGHT:
      return DEFAULT_LIGHT_THEME;
    case THEME_TYPES.CUSTOM:
      return EMPTY_CUSTOM_THEME;
    case THEME_TYPES.DARK:
    default:
      return DEFAULT_DARK_THEME;
  }
}
